import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, ExternalLink, Image as ImageIcon, Database, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

export default function KimKimAddonsBrowser({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("__all__");
  const [destination, setDestination] = useState("__all__");
  const [city, setCity] = useState("__all__");
  const [onlyWith, setOnlyWith] = useState("__all__");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const facetsQ = useQuery({
    queryKey: ["kimkim-facets"],
    queryFn: async () => (await api.get("/admin/kimkim/addons/facets")).data,
    enabled: open,
    staleTime: 60_000,
  });

  const listQ = useQuery({
    queryKey: ["kimkim-addons-list", q, type, destination, city, onlyWith, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (q.trim()) params.set("q", q.trim());
      if (type !== "__all__") params.set("type", type);
      if (destination !== "__all__") params.set("destination", destination);
      if (city !== "__all__") params.set("city", city);
      if (onlyWith !== "__all__") params.set("only_with", onlyWith);
      return (await api.get(`/admin/kimkim/addons?${params.toString()}`)).data;
    },
    enabled: open,
    keepPreviousData: true,
    refetchInterval: 15_000,
  });

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const pages = listQ.data?.pages || 0;

  const priceFromTable = (row) => {
    const t = row.pricing_table;
    if (!t || !t.length) return row.pricing_display || "—";
    // Try to find something with EUR/€
    for (const r of t) {
      const s = Array.isArray(r) ? r.join(" ") : String(r);
      const m = s.match(/(\d[\d.,]*)\s*(EUR|€|USD|\$)/i);
      if (m) return `${m[1]} ${m[2] === "$" ? "USD" : m[2].toUpperCase()}`;
    }
    return row.pricing_display || "—";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="kimkim-browser-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Explorador de addons KimKim
            <Badge variant="secondary">
              {total.toLocaleString("es-ES")} de{" "}
              {(facetsQ.data?.types || []).reduce((s, t) => s + t.count, 0).toLocaleString("es-ES")}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-5 border-b pb-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, descripción, destino, ciudad, notas…"
              className="pl-8"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              data-testid="kimkim-browser-search"
            />
          </div>
          <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
            <SelectTrigger data-testid="kimkim-browser-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los tipos</SelectItem>
              {(facetsQ.data?.types || []).map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.value} ({t.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={destination} onValueChange={(v) => { setDestination(v); setPage(1); }}>
            <SelectTrigger data-testid="kimkim-browser-destination"><SelectValue placeholder="País" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all__">Todos los países</SelectItem>
              {(facetsQ.data?.destinations || []).map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.value} ({d.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={(v) => { setCity(v); setPage(1); }}>
            <SelectTrigger data-testid="kimkim-browser-city"><SelectValue placeholder="Ciudad" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all__">Todas las ciudades</SelectItem>
              {(facetsQ.data?.cities || []).map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.value} ({c.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-5 pb-2">
          <Select value={onlyWith} onValueChange={(v) => { setOnlyWith(v); setPage(1); }}>
            <SelectTrigger><SelectValue placeholder="Datos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              <SelectItem value="description">Solo con descripción</SelectItem>
              <SelectItem value="images">Solo con fotos</SelectItem>
              <SelectItem value="pricing">Solo con precios</SelectItem>
              <SelectItem value="notes">Solo con notas internas</SelectItem>
              <SelectItem value="mapped_operator">Proveedor mapeado</SelectItem>
              <SelectItem value="unmatched_operator">Proveedor sin mapear</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Layout tabla + detalle */}
        <div className="flex flex-1 gap-3 overflow-hidden">
          <div className="flex-1 overflow-auto rounded border">
            {listQ.isLoading ? (
              <div className="flex h-40 items-center justify-center text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Sin resultados con estos filtros.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="p-2 text-left">Nombre</th>
                    <th className="p-2 text-left">Tipo</th>
                    <th className="p-2 text-left">Ciudades</th>
                    <th className="p-2 text-left">Proveedor</th>
                    <th className="p-2 text-left">Duración</th>
                    <th className="p-2 text-right">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.addon_id}
                      className={`cursor-pointer border-t hover:bg-slate-50 ${selected?.addon_id === row.addon_id ? "bg-blue-50" : ""}`}
                      onClick={() => setSelected(row)}
                      data-testid={`kimkim-row-${row.addon_id}`}
                    >
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {row.main_image ? (
                            <img src={row.main_image} alt="" className="h-8 w-8 rounded object-cover" />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded bg-slate-100">
                              <ImageIcon className="h-3 w-3 text-slate-400" />
                            </div>
                          )}
                          <span className="line-clamp-1">{row.name}</span>
                        </div>
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-xs">{row.type || "—"}</Badge>
                      </td>
                      <td className="p-2 text-xs text-slate-700">
                        {(row.cities || []).length > 1 ? (
                          <div className="flex flex-wrap gap-0.5" data-testid={`kimkim-cities-${row.addon_id}`}>
                            {row.cities.slice(0, 4).map((ci, idx) => (
                              <span key={idx} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">{ci}</span>
                            ))}
                            {row.cities.length > 4 && <span className="text-[10px] text-slate-500">+{row.cities.length - 4}</span>}
                          </div>
                        ) : (
                          <>{row.destination || "—"}
                            {row.location ? <div className="text-[10px] text-slate-500 line-clamp-1">{row.location}</div> : null}
                          </>
                        )}
                      </td>
                      <td className="p-2 text-xs text-slate-700" data-testid={`kimkim-operator-${row.addon_id}`}>
                        {row.operator ? (
                          row.operator_name ? (
                            <span
                              className="line-clamp-2 rounded border border-emerald-400 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800"
                              title={row.operator_email ? `${row.operator} · ${row.operator_email}` : row.operator}
                            >
                              {row.operator_name}
                            </span>
                          ) : (
                            <span
                              className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700"
                              title="Sin mapear en el maestro"
                            >
                              {row.operator}
                            </span>
                          )
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-2 text-xs text-slate-700">{row.duration_detail || row.duration || "—"}</td>
                      <td className="p-2 text-right text-xs font-medium tabular-nums text-slate-900">{priceFromTable(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detalle */}
          <div className="hidden w-96 flex-shrink-0 overflow-y-auto rounded border p-3 md:block">
            {!selected ? (
              <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
                Selecciona una fila para ver su detalle.
              </div>
            ) : (
              <div className="space-y-3" data-testid="kimkim-detail-panel">
                {selected.main_image && (
                  <img src={selected.main_image} alt="" className="h-40 w-full rounded object-cover" />
                )}
                <div>
                  <h3 className="text-base font-semibold leading-snug">{selected.name}</h3>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant="outline">{selected.type}</Badge>
                    {(selected.cities || []).length > 0 ? (
                      selected.cities.map((ci, idx) => (
                        <Badge key={idx} variant="secondary" data-testid={`kimkim-detail-city-${idx}`}>{ci}</Badge>
                      ))
                    ) : (selected.destination && <Badge variant="outline">{selected.destination}</Badge>)}
                    {selected.duration_detail && <Badge variant="outline">{selected.duration_detail}</Badge>}
                    {selected.group_type && <Badge variant="outline">{selected.group_type}</Badge>}
                  </div>
                  {selected.operator && (
                    <div className="mt-2 flex flex-col gap-1 rounded border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="kimkim-detail-operator">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Operado por:</span>
                        <span className={`rounded border px-2 py-0.5 font-semibold uppercase tracking-wide ${selected.operator_name ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-800"}`}>{selected.operator}</span>
                        {selected.operator_source === "notes" && (
                          <span className="text-[10px] text-slate-400" title="Extraído de las notas internas de precio">notas</span>
                        )}
                      </div>
                      {selected.operator_name ? (
                        <div className="text-sm font-medium text-slate-900" data-testid="kimkim-detail-operator-name">{selected.operator_name}</div>
                      ) : (
                        <div className="text-[11px] italic text-amber-700">Proveedor sin mapear en el maestro de Excel</div>
                      )}
                      {selected.operator_email && (
                        <a href={`mailto:${selected.operator_email}`} className="truncate text-[11px] text-blue-600 hover:underline">{selected.operator_email}</a>
                      )}
                      {selected.operator_contact && (
                        <div className="text-[11px] text-slate-600">{selected.operator_contact}</div>
                      )}
                      {selected.operator_website && (
                        <a href={selected.operator_website} target="_blank" rel="noreferrer" className="truncate text-[11px] text-blue-600 hover:underline">{selected.operator_website}</a>
                      )}
                      {selected.operator_country && (
                        <div className="text-[10px] text-slate-500">País del proveedor: {selected.operator_country}{selected.operator_city_source ? ` · ${selected.operator_city_source}` : ""}</div>
                      )}
                    </div>
                  )}
                </div>
                {selected.location && (
                  <div className="text-xs"><strong>Ubicación:</strong> {selected.location}</div>
                )}
                {selected.meals && selected.meals !== "None" && (
                  <div className="text-xs"><strong>Comidas:</strong> {selected.meals}</div>
                )}
                {selected.description && (
                  <div className="text-xs">
                    <strong>Descripción:</strong>
                    <p className="mt-1 line-clamp-6 whitespace-pre-wrap text-slate-700">{selected.description}</p>
                  </div>
                )}
                {selected.pricing_table?.length > 0 && (
                  <div className="text-xs">
                    <strong>Precios:</strong>
                    <ul className="mt-1 space-y-0.5 text-slate-700">
                      {selected.pricing_table.slice(0, 8).map((row, i) => (
                        <li key={i} className="border-l-2 border-slate-200 pl-2">{row.join(" · ")}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selected.pricing_notes && (
                  <div className="rounded bg-amber-50 p-2 text-xs" data-testid="kimkim-browser-pricing-notes">
                    <strong className="text-amber-800">📝 Notas internas de precio:</strong>
                    <p className="mt-1 whitespace-pre-wrap text-amber-900">{selected.pricing_notes}</p>
                  </div>
                )}
                {selected.whats_included && (
                  <div className="text-xs">
                    <strong>Qué incluye:</strong>
                    <p className="mt-1 line-clamp-4 text-slate-700">{selected.whats_included}</p>
                  </div>
                )}
                {selected.images?.length > 1 && (
                  <div className="grid grid-cols-3 gap-1">
                    {selected.images.slice(0, 6).map((src, i) => (
                      <img key={i} src={src} alt="" className="h-16 w-full rounded object-cover" />
                    ))}
                  </div>
                )}
                <a
                  href={`https://www.kimkim.com${selected.url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Abrir en KimKim
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between border-t pt-2 text-sm">
          <div className="text-xs text-muted-foreground">
            Página {page} de {pages || 1} · {total.toLocaleString("es-ES")} resultados
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="kimkim-browser-prev">
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="kimkim-browser-next">
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
